import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicPageShell from '../components/layout/PublicPageShell';
import MemberShell from '../components/layout/MemberShell';
import { Spinner } from '../components/ui';

/**
 * The 404 is a recovery surface, not a report.
 *
 * It used to render PATIENT_PRIMARY_PATHS — an internal ownership table — so a
 * member who mistyped a URL was shown "Owner: Growth / Brand" and
 * "Owner: Clinical Product" under each link, above a line explaining that Melio
 * surfaces dead routes "so they can be cleaned up". That is engineering
 * bookkeeping pointed at a patient. It also offered a signed-in member /login,
 * /signup and /enrollment, none of which are anywhere they want to go.
 *
 * Destinations are now split by who is asking, and the copy says the one useful
 * thing: nothing is lost, here is the way back.
 */

interface Destination {
  path: string;
  label: string;
  hint: string;
}

const MEMBER_DESTINATIONS: Destination[] = [
  { path: '/home', label: 'Home', hint: 'Your next step and what has changed' },
  { path: '/plan', label: 'Your plan', hint: 'What the practice is watching' },
  { path: '/reports', label: 'Reports & documents', hint: 'Quarterly reports and your files' },
  { path: '/intelligence', label: 'Results', hint: 'Your biomarkers and trends' },
  { path: '/messages', label: 'Messages', hint: 'Talk to your care team' },
  { path: '/profile', label: 'Profile', hint: 'Membership, billing and details' },
];

const PUBLIC_DESTINATIONS: Destination[] = [
  { path: '/', label: 'Home', hint: 'About the practice' },
  { path: '/membership', label: 'Membership', hint: 'What being a member includes' },
  { path: '/faq', label: 'Questions', hint: 'How Melio works' },
  { path: '/login', label: 'Sign in', hint: 'Already a member' },
];

function NotFoundContent({
  destinations,
  signedIn,
}: {
  destinations: Destination[];
  signedIn: boolean;
}) {
  return (
    <div
      className="rounded-3xl border p-8 md:p-10"
      style={{ borderColor: 'var(--m-line)', backgroundColor: 'var(--m-surface)' }}
    >
      <p className="m-eyebrow mb-3">Page not found</p>
      <h1 className="m-display-1 mb-4">This page doesn&apos;t exist.</h1>
      <p className="text-sm md:text-base mb-8 max-w-2xl" style={{ color: 'var(--melio-text-muted)' }}>
        {signedIn
          ? 'Nothing has happened to your record — the link was just wrong. Here is where you might have been heading.'
          : 'The link may be out of date. Here is where you might have been heading.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {destinations.map((destination) => (
          <Link
            key={destination.path}
            to={destination.path}
            className="rounded-2xl border p-4 melio-lift"
            style={{ borderColor: 'var(--m-line)', backgroundColor: 'var(--m-surface-2)' }}
          >
            <p className="font-medium" style={{ color: 'var(--m-ink)' }}>
              {destination.label}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
              {destination.hint}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function NotFoundPage() {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (currentUser) {
    return (
      <MemberShell>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <NotFoundContent destinations={MEMBER_DESTINATIONS} signedIn />
        </div>
      </MemberShell>
    );
  }

  return (
    <PublicPageShell backHref="/" backLabel="Back to home" contentClassName="max-w-5xl mx-auto">
      <NotFoundContent destinations={PUBLIC_DESTINATIONS} signedIn={false} />
    </PublicPageShell>
  );
}
